/**
 * Refresh the browser's HTTP-cache copies of the files Gatsby hydrates from.
 *
 * WHY THIS EXISTS (2026-09-10). www.duffloo.com is served by LiteSpeed with NO
 * `Cache-Control` on `page-data.json` / `app-data.json`, so browsers apply the
 * heuristic (roughly 10% of the time since `Last-Modified`). A visitor who
 * loaded the previous site kept its `/page-data/terms-and-conditions/page-data.json`
 * — dated 2024-11-03 — for months. Gatsby rendered the fresh HTML (September
 * 2026 Terms), then hydrated from that cached file (the October 2023 document),
 * saw the build-hash mismatch and reloaded, and the reload hydrated from the
 * same cached file. Every returning visitor saw the 2023 Terms; curl saw the
 * right ones. `static/.htaccess` now sends the headers Gatsby documents, but a
 * header cannot reach an entry a browser already considers fresh.
 *
 * `fetch(url, {cache: "reload"})` bypasses the cache AND replaces the entry.
 * `replaceHydrateFunction` awaits this before `root.render`, so the first
 * client render uses the current build instead of flashing the cached 2023
 * Terms. In-app navigations refresh again so the next full load stays current.
 */

/** @param {string} pathname A location pathname. @return {string} Gatsby's page-data URL for it. */
export const pageDataUrlFor = (pathname) => {
  const trimmed = String(pathname || "/").replace(/^\/+|\/+$/g, "");
  return `/page-data/${trimmed === "" ? "index" : trimmed}/page-data.json`;
};

export const APP_DATA_URL = "/page-data/app-data.json";

/**
 * Re-fetch this path's page-data and app-data, bypassing and replacing the
 * HTTP cache. Never throws; a failed refresh just leaves the cache as it was.
 *
 * @param {string} pathname The path being loaded.
 * @param {typeof fetch} [fetchImpl] Injectable for tests.
 * @return {Promise<void>} Resolves when both requests have settled.
 */
export const refreshPageDataCache = (pathname, fetchImpl) => {
  const f = fetchImpl || (typeof fetch === "function" ? fetch : null);
  if (!f) return Promise.resolve();
  const opts = { cache: "reload", credentials: "same-origin" };
  return Promise.allSettled([
    f(pageDataUrlFor(pathname), opts),
    f(APP_DATA_URL, opts),
  ]).then(() => undefined);
};
